'use client';

import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { PostHogProvider } from '@miadvg/posthog/provider';
import { usePathname } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import {
  clearOpportunitySuperProperty,
  getOpportunityIdFromPathname,
  syncOpportunityPropertyOnAutomaticEvent,
  syncOpportunitySuperProperty,
} from '@/lib/analytics/opportunity-super-property';
import type { PostHogSuperPropertyClient } from '@/lib/analytics/opportunity-super-property';

function OpportunitySuperPropertyLifecycle({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const posthog = usePostHog();

  useEffect(() => {
    syncOpportunitySuperProperty(posthog, getOpportunityIdFromPathname(pathname));
  }, [pathname, posthog]);

  useEffect(() => () => clearOpportunitySuperProperty(posthog), [posthog]);

  return children;
}

export function RedPostHogProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const registerInitialOpportunity = useCallback(
    (posthog: PostHogSuperPropertyClient) => {
      syncOpportunitySuperProperty(posthog, getOpportunityIdFromPathname(pathname));
    },
    [pathname],
  );
  const options = useMemo(
    () => ({
      api_host: '/ingest',
      ui_host: 'https://us.i.posthog.com',
      before_send: syncOpportunityPropertyOnAutomaticEvent,
      capture_pageview: true,
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '.ph-no-capture, [data-sensitive], input[type="password"]',
      },
      loaded: registerInitialOpportunity,
    }),
    [registerInitialOpportunity],
  );

  return (
    <PostHogProvider options={options}>
      <OpportunitySuperPropertyLifecycle>{children}</OpportunitySuperPropertyLifecycle>
    </PostHogProvider>
  );
}
